"use strict";
// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
Object.defineProperty(exports, "__esModule", { value: true });
exports.DeclarativeAgentManifestWrapper = exports.CapabilityName = void 0;
const generated_types_1 = require("../generated-types");
const BaseManifest_1 = require("./BaseManifest");
/**
 * Capability names supported by Declarative Agents.
 * These values match the auto-generated Name type from the schema.
 * @see CapabilityNameValue for the type definition
 */
exports.CapabilityName = {
    WebSearch: "WebSearch",
    GraphicArt: "GraphicArt",
    CodeInterpreter: "CodeInterpreter",
    OneDriveAndSharePoint: "OneDriveAndSharePoint",
    GraphConnectors: "GraphConnectors",
    EmbeddedKnowledge: "EmbeddedKnowledge",
    TeamsMessages: "TeamsMessages",
    Dataverse: "Dataverse",
    Email: "Email",
    People: "People",
    Meetings: "Meetings",
    ScenarioModels: "ScenarioModels",
};
/**
 * OOP wrapper for Declarative Agent Manifest.
 *
 * Provides a fluent API for manipulating declarative agent manifests with
 * type safety, state tracking, and convenient operations.
 *
 * @example
 * ```typescript
 * // Read existing manifest
 * const agent = await DeclarativeAgentManifestWrapper.read("agent.json");
 *
 * // Modify with fluent API
 * agent
 *   .setInstructions("You are a helpful assistant...")
 *   .addAction("action1", "plugin.json")
 *   .addWebSearchCapability([{ url: "https://docs.microsoft.com" }])
 *   .addConversationStarter("How can I help you today?");
 *
 * // Save changes
 * await agent.save();
 * ```
 */
class DeclarativeAgentManifestWrapper extends BaseManifest_1.BaseManifest {
    constructor(data, filePath) {
        super(data, filePath);
    }
    // ============= Static Factory Methods =============
    /**
     * Reads a declarative agent manifest from a file.
     * @param filePath - Path to the manifest JSON file.
     * @returns A new DeclarativeAgentManifestWrapper instance.
     */
    static async read(filePath) {
        const data = await generated_types_1.AppManifestUtils.readDeclarativeAgentManifest(filePath);
        return new DeclarativeAgentManifestWrapper(data, filePath);
    }
    /**
     * Reads a declarative agent manifest from a file synchronously.
     * @param filePath - Path to the manifest JSON file.
     * @returns A new DeclarativeAgentManifestWrapper instance.
     */
    static readSync(filePath) {
        const json = BaseManifest_1.BaseManifest.readJsonFileSync(filePath);
        const data = generated_types_1.DeclarativeAgentManifestConverter.jsonToManifest(JSON.stringify(json));
        return new DeclarativeAgentManifestWrapper(data, filePath);
    }
    /**
     * Creates a DeclarativeAgentManifestWrapper from a JSON string.
     * @param json - JSON string representing the manifest.
     * @returns A new DeclarativeAgentManifestWrapper instance.
     */
    static fromJSON(json) {
        const data = generated_types_1.DeclarativeAgentManifestConverter.jsonToManifest(json);
        return new DeclarativeAgentManifestWrapper(data);
    }
    /**
     * Creates a new declarative agent manifest with required fields.
     * @param init - Initial manifest data with required fields.
     * @returns A new DeclarativeAgentManifestWrapper instance.
     */
    static create(init) {
        const data = {
            $schema: `https://developer.microsoft.com/json-schemas/copilot/declarative-agent/${init.version}/schema.json`,
            ...init,
        };
        return new DeclarativeAgentManifestWrapper(data);
    }
    // ============= Getters =============
    /**
     * Returns the version of the manifest.
     */
    get version() {
        return this._data.version;
    }
    /**
     * Returns the name of the agent.
     */
    get name() {
        return this._data.name;
    }
    /**
     * Returns the description of the agent.
     */
    get description() {
        return this._data.description;
    }
    /**
     * Returns the instructions for the agent.
     */
    get instructions() {
        return this._data.instructions;
    }
    /**
     * Returns a readonly array of actions.
     */
    get actions() {
        return this._data.actions ?? [];
    }
    /**
     * Returns a readonly array of capabilities.
     */
    get capabilities() {
        return this._data.capabilities ?? [];
    }
    /**
     * Returns a readonly array of conversation starters.
     */
    get conversationStarters() {
        return this._data.conversation_starters ?? [];
    }
    /**
     * Returns a readonly array of worker agents.
     */
    get workerAgents() {
        return this._data.worker_agents ?? [];
    }
    // ============= Setters (Fluent API) =============
    /**
     * Sets the name of the agent.
     */
    setName(name) {
        this._data.name = name;
        this.markDirty();
        return this;
    }
    /**
     * Sets the description of the agent.
     */
    setDescription(description) {
        this._data.description = description;
        this.markDirty();
        return this;
    }
    /**
     * Sets the instructions for the agent.
     */
    setInstructions(instructions) {
        this._data.instructions = instructions;
        this.markDirty();
        return this;
    }
    // ============= Action Operations =============
    /**
     * Adds an action (API plugin) to the agent.
     * @param id - Unique identifier for the action.
     * @param file - Relative path to the plugin manifest file.
     */
    addAction(id, file) {
        if (!this._data.actions) {
            this._data.actions = [];
        }
        this._data.actions.push({ id, file });
        this.markDirty();
        return this;
    }
    /**
     * Removes an action by ID.
     * @param id - The ID of the action to remove.
     */
    removeAction(id) {
        if (this._data.actions) {
            this._data.actions = this._data.actions.filter((a) => a.id !== id);
            this.markDirty();
        }
        return this;
    }
    /**
     * Checks if an action exists by ID.
     */
    hasAction(id) {
        return this.actions.some((a) => a.id === id);
    }
    /**
     * Gets an action by ID.
     */
    getAction(id) {
        return this.actions.find((a) => a.id === id);
    }
    /**
     * Returns all action plugin file paths.
     */
    getActionPluginPaths() {
        return this.actions.map((a) => a.file);
    }
    // ============= Capability Operations =============
    /**
     * Adds or updates a capability.
     * If a capability with the same name exists, it will be replaced.
     * @param capability - The capability to add or update.
     */
    addCapability(capability) {
        if (!this._data.capabilities) {
            this._data.capabilities = [];
        }
        const capabilities = this._data.capabilities;
        const existingIndex = capabilities.findIndex((c) => c.name === capability.name);
        if (existingIndex >= 0) {
            capabilities[existingIndex] = capability;
        }
        else {
            capabilities.push(capability);
        }
        this.markDirty();
        return this;
    }
    /**
     * Removes a capability by name.
     * @param name - The name of the capability to remove.
     */
    removeCapability(name) {
        if (this._data.capabilities) {
            this._data.capabilities = this._data.capabilities.filter((c) => c.name !== name);
            this.markDirty();
        }
        return this;
    }
    /**
     * Checks if a capability exists by name.
     */
    hasCapability(name) {
        return this.capabilities.some((c) => c.name === name);
    }
    /**
     * Gets a capability by name.
     */
    getCapability(name) {
        return this.capabilities.find((c) => c.name === name);
    }
    // ============= Convenience Capability Methods =============
    /**
     * Adds or updates the WebSearch capability.
     * @param sites - Optional array of site URLs to constrain search.
     */
    addWebSearchCapability(sites) {
        return this.addCapability({
            name: exports.CapabilityName.WebSearch,
            sites,
        });
    }
    /**
     * Adds or updates the OneDriveAndSharePoint capability.
     * @param options - Configuration for SharePoint/OneDrive sources.
     */
    addOneDriveSharePointCapability(options) {
        return this.addCapability({
            name: exports.CapabilityName.OneDriveAndSharePoint,
            ...options,
        });
    }
    /**
     * Adds or updates the GraphConnectors capability.
     * @param connectionIds - Array of Graph connector connection IDs.
     */
    addGraphConnectorsCapability(connectionIds) {
        const connections = connectionIds.map((id) => ({ connection_id: id }));
        return this.addCapability({
            name: exports.CapabilityName.GraphConnectors,
            connections,
        });
    }
    /**
     * Adds or updates the EmbeddedKnowledge capability.
     * @param files - Array of embedded knowledge files.
     */
    addEmbeddedKnowledgeCapability(files) {
        return this.addCapability({
            name: exports.CapabilityName.EmbeddedKnowledge,
            files,
        });
    }
    /**
     * Adds or updates the CodeInterpreter capability.
     */
    addCodeInterpreterCapability() {
        return this.addCapability({
            name: exports.CapabilityName.CodeInterpreter,
        });
    }
    /**
     * Adds or updates the GraphicArt capability.
     */
    addGraphicArtCapability() {
        return this.addCapability({
            name: exports.CapabilityName.GraphicArt,
        });
    }
    // ============= Conversation Starter Operations =============
    /**
     * Adds a conversation starter.
     * Maximum 12 starters are allowed.
     * @param text - The text of the conversation starter.
     * @param title - Optional title for the starter.
     */
    addConversationStarter(text, title) {
        if (!this._data.conversation_starters) {
            this._data.conversation_starters = [];
        }
        const starters = this._data.conversation_starters;
        if (starters.length >= 12) {
            console.warn("Maximum 12 conversation starters allowed. Ignoring addition.");
            return this;
        }
        // Avoid duplicates
        if (!starters.some((s) => s.text === text)) {
            starters.push({ text, title });
            this.markDirty();
        }
        return this;
    }
    /**
     * Removes a conversation starter by text.
     */
    removeConversationStarter(text) {
        if (this._data.conversation_starters) {
            this._data.conversation_starters = this._data.conversation_starters.filter((s) => s.text !== text);
            this.markDirty();
        }
        return this;
    }
    /**
     * Clears all conversation starters.
     */
    clearConversationStarters() {
        this._data.conversation_starters = [];
        this.markDirty();
        return this;
    }
    // ============= Worker Agent Operations =============
    /**
     * Adds a worker agent.
     * @param id - The ID of the worker agent.
     */
    addWorkerAgent(id) {
        const data = this._data;
        if (!data.worker_agents) {
            data.worker_agents = [];
        }
        if (!data.worker_agents.some((w) => w.id === id)) {
            data.worker_agents.push({ id });
            this.markDirty();
        }
        return this;
    }
    /**
     * Removes a worker agent by ID.
     */
    removeWorkerAgent(id) {
        const data = this._data;
        if (data.worker_agents) {
            data.worker_agents = data.worker_agents.filter((w) => w.id !== id);
            this.markDirty();
        }
        return this;
    }
    // ============= Validation =============
    /**
     * Validates the manifest against its JSON schema.
     * @returns Array of validation error messages, empty if valid.
     */
    async validate() {
        return generated_types_1.AppManifestUtils.validateAgainstSchema(this._data);
    }
    // ============= Serialization =============
    /**
     * Converts the manifest to a formatted JSON string.
     */
    toJSON() {
        return generated_types_1.DeclarativeAgentManifestConverter.manifestToJson(this._data);
    }
    /**
     * Creates a deep clone of this manifest.
     */
    clone() {
        const clonedData = JSON.parse(this.toJSON());
        return new DeclarativeAgentManifestWrapper(clonedData);
    }
}
exports.DeclarativeAgentManifestWrapper = DeclarativeAgentManifestWrapper;
//# sourceMappingURL=DeclarativeAgentManifestWrapper.js.map